


import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import { toast } from "react-toastify";
import Swal from 'sweetalert2'; 
import ChatPdfDocument from './ChatPdfDocument';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { 
  FaDownload, 
  FaTrash, 
  FaChevronDown, 
  FaChevronUp, 
  FaSpinner,
  FaUsers,
  FaComments,
  FaFilePdf,
  FaExclamationTriangle
} from 'react-icons/fa';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// Use Font Awesome icons instead of custom SVG components
const TrashIcon = () => <FaTrash className="h-5 w-5" />;
const SpinnerIcon = () => <FaSpinner className="animate-spin h-5 w-5 text-gray-400" />;


const UserChatHistory = () => {
    const [usersData, setUsersData] = useState([]);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [expandedUser, setExpandedUser] = useState(null);
    const [expandedChat, setExpandedChat] = useState(null);
    const [processingSessionId, setProcessingSessionId] = useState(null);
    const [isDeleting, setIsDeleting] = useState({ id: null, type: null });

    const fetchUsers = useCallback(async (page, limit) => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const response = await axios.get(`${API_BASE_URL}/admin/all-users`, {
                headers: headers,
                params: { page, limit },
            });

            if (response?.data) {
                setUsersData(response.data.data || []);
                setCurrentPage(response.data.page || 1);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
                if (response.data.data.length === 0 && response.data.totalItems > 0 && page > 1) {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                }
            } else {
                setUsersData([]); setTotalPages(0); setTotalItems(0);
                setError('No data received from server.');
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
            setError(err.response?.data?.message || err.message || 'An unexpected error occurred');
            setUsersData([]); setTotalPages(0); setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers(currentPage, itemsPerPage);
    }, [currentPage, itemsPerPage, fetchUsers]);


    const handleDeleteUser = async (userId, userEmail) => {
        const result = await Swal.fire({
            title: `Delete user ${userEmail}?`,
            text: "This will permanently delete the user and all their chat histories. This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete user!',
            cancelButtonText: 'Cancel',
            customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
        });

        if (result.isConfirmed) {
            setIsDeleting({ id: userId, type: 'user' });
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                await axios.delete(`${API_BASE_URL}/admin/delete-user/${userId}`, { headers });
                
                toast.success('User deleted successfully.');
                fetchUsers(currentPage, itemsPerPage); // Re-fetch to update the list
            } catch (err) {
                console.error('Error deleting user:', err);
                toast.error(err.response?.data?.metadata?.message || 'Failed to delete user.');
            } finally {
                setIsDeleting({ id: null, type: null });
            }
        }
    };

    const handleDeleteSession = async (userId, sessionId) => {
        const result = await Swal.fire({
            title: 'Delete this chat session?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
             customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
        });

        if (result.isConfirmed) {
            setIsDeleting({ id: sessionId, type: 'session' });
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                await axios.delete(`${API_BASE_URL}/admin/${userId}/session/${sessionId}`, { headers });

                toast.success('Chat session deleted successfully.');
                
                // Update state locally for a better UX (no full reload)
                setUsersData(prevUsers => 
                    prevUsers.map(user => {
                        if (user._id === userId) {
                            return {
                                ...user,
                                chats: user.chats.filter(chat => chat.sessionId !== sessionId)
                            };
                        }
                        return user;
                    })
                );
            } catch (err) {
                console.error('Error deleting session:', err);
                toast.error(err.response?.data?.metadata?.message || 'Failed to delete session.');
            } finally {
                setIsDeleting({ id: null, type: null });
            }
        }
    };


const handleDownloadAllPdfs = async () => {
  setIsDownloadingAll(true);
  const toastId = toast.loading("Preparing PDFs for download...");
  
  try {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await axios.post(
      `${API_BASE_URL}/admin/download-chat-pdfs`,
      { page: currentPage },
      {
        headers: headers,
        responseType: 'blob' // Important for file download
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `all_chat_pdfs_page_${currentPage}.zip`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);

    toast.update(toastId, {
      render: `Downloaded PDFs successfully!`,
      type: "success",
      isLoading: false,
      autoClose: 3000
    });
  } catch (error) {
    console.error("Error downloading PDFs:", error);
    toast.update(toastId, {
      render: `Failed to download PDFs: ${error.message}`,
      type: "error",
      isLoading: false,
      autoClose: 5000
    });
  } finally {
    setIsDownloadingAll(false);
  }
};


const handleDeleteAllConversations = async () => {
    const result = await Swal.fire({
        title: 'Are you absolutely sure?',
        text: "You want to delete ALL chat histories for ALL users? This action cannot be undone.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33', // Dark red
        cancelButtonColor: '#3085d6', // Blue
        confirmButtonText: 'Yes, delete all!',
        cancelButtonText: 'Cancel',
        customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
    });
    if (result.isConfirmed) {
        setIsDeleting({ id: 'all', type: 'all' });
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            await axios.delete(`${API_BASE_URL}/user/delete-all-conversation`, { headers });
            toast.success('All chat histories deleted successfully.');
            fetchUsers(1, itemsPerPage);
            setExpandedUser(null);
            setExpandedChat(null);
        } catch (err) {
            console.error('Error deleting all chat histories:', err);
            toast.error(err.response?.data?.metadata?.message || err.message || 'Failed to delete all chat histories.');
        } finally {
            setIsDeleting({ id: null, type: null });
        }
    }
};

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !isLoading) {
          setCurrentPage(newPage);
          setExpandedUser(null);
          setExpandedChat(null);
          setProcessingSessionId(null); 
        }
    };
    
    const handleItemsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setItemsPerPage(newLimit);
        setCurrentPage(1);
        setExpandedUser(null);
        setExpandedChat(null);
        setProcessingSessionId(null); 
    };
    
    const toggleUserExpansion = (userId) => {
        setExpandedUser(expandedUser === userId ? null : userId);
        setExpandedChat(null);
        setProcessingSessionId(null); 
    };
    
    const toggleChatExpansion = (sessionId) => {
        setExpandedChat(expandedChat === sessionId ? null : sessionId);
    };
    
    // const handleExportChatPdf = async (chat, guestId, userName) => {
    //     if (!chat || !chat.conversation || chat.conversation.length === 0) {
    //         toast.error("No conversation data available for export.");
    //         return;
    //     }
    
    //     setProcessingSessionId(chat.sessionId); 
    //     const toastId = toast.loading("Preparing chat PDF...", { id: `exportToast-${chat.sessionId}` }); 
    
    //     try {
    //         const pdfBlob = await pdf(<ChatPdfDocument chat={chat} userName={userName} />).toBlob();
    //         // Change toast message to reflect opening in new tab
    //         toast.update(toastId, { render: "Generated PDF. Now opening in new tab and uploading..." });
    
    //         const filename = `chat_session_${chat.sessionId}.pdf`;
    //         const url = URL.createObjectURL(pdfBlob);
    //         const link = document.createElement('a');
    //         link.href = url;
    //         link.target = '_blank'; // Open in a new tab
    //         // REMOVED: link.download = filename; // This line triggers the "Save As" dialog for downloading
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //         URL.revokeObjectURL(url);
    
    //         // Update toast message for successful opening
    //         toast.update(toastId, { render: "PDF opened in new tab.", type: "success", isLoading: false, autoClose: 3000 });
    
    //         const formData = new FormData();
    //         formData.append('pdfFile', pdfBlob, filename);
    //         formData.append('sessionId', chat.sessionId);
    //         formData.append('guestId', guestId);
    
    //         const token = localStorage.getItem('token');
    //         const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    //         const uploadResponse = await axios.post(`${API_BASE_URL}/admin/upload-chat-pdf`, formData, {
    //             headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    //         });
    
    //         if (uploadResponse?.data?.s3Url) {
    //             // toast.success(`PDF also uploaded to S3 successfully!`, { duration: 4000 });
    //             console.log("Uploaded to S3:", uploadResponse.data.s3Url);
    //         } else {
    //             // Adjust warning message as well
    //             toast.warn("PDF opened, but S3 upload finished with no URL.", { duration: 4000 });
    //         }
    
    //     } catch (err) {
    //         console.error('Error during chat export:', err);
    //         toast.error(err.response?.data?.message || err.message || 'Failed to export chat PDF', { id: toastId });
    //     } finally {
    //         setProcessingSessionId(null); 
    //     }
    // };
      

    const handleExportChatPdf = (chat) => {
  if (!chat.pdfS3Url) {
    toast.error("PDF not available for this chat.");
    return;
  }
  
  // Open the PDF in a new tab
  window.open(chat.pdfS3Url, '_blank');
};

    const PaginationControls = () => {
        // ... (No changes needed in this function, it's fine as is)
        if (!totalPages || totalPages <= 0) return null;
    
        const getPageNumbers = () => {
            const pageNumbers = [];
            const maxPagesToShow = 5;
            const halfMaxPages = Math.floor(maxPagesToShow / 2);
    
            if (totalPages <= maxPagesToShow) {
                for (let i = 1; i <= totalPages; i++) { pageNumbers.push(i); }
            } else {
                let startPage = Math.max(1, currentPage - halfMaxPages);
                let endPage = startPage + maxPagesToShow - 1;
    
                if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = endPage - maxPagesToShow + 1;
                }
    
                if (startPage > 1) {
                    pageNumbers.push(1);
                    if (startPage > 2) { pageNumbers.push('...'); }
                }
                for (let i = startPage; i <= endPage; i++) { pageNumbers.push(i); }
                if (endPage < totalPages) {
                    if (endPage < totalPages - 1) { pageNumbers.push('...'); }
                    pageNumbers.push(totalPages);
                }
            }
            return pageNumbers;
        };
        
        const pageDisplayInfo = totalItems > 0 ? `Page ${currentPage} of ${totalPages} (Total: ${totalItems} users)` : `Page ${currentPage} of ${totalPages}`;
    
        return (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
                 <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 sm:mb-0 flex items-center space-x-4">
                    <span>{pageDisplayInfo}</span>
                    <div className="flex items-center space-x-2">
                        <span className="sr-only sm:not-sr-only">Items per page:</span>
                        <select 
                          value={itemsPerPage} 
                          onChange={handleItemsPerPageChange}
                          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                    </div>
                </div>
                {totalPages > 1 && (
                    <nav className="inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className={`relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed`}>Previous</button>
                        {getPageNumbers().map((page, index) => (
                            typeof page === 'number' ? (
                                <button key={index} onClick={() => handlePageChange(page)} disabled={isLoading} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium ${currentPage === page ? 'z-10 bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>{page}</button>
                            ) : (
                                <span key={index} className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">{page}</span>
                            )
                        ))}
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className={`relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed`}>Next</button>
                    </nav>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-full">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Chat History
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            View and manage user conversations and chat sessions
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleDownloadAllPdfs}
                            disabled={isDeleting.type === 'all' || totalItems === 0} 
                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl shadow-lg font-medium text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                                ${isDeleting.type === 'all' || totalItems === 0
                                    ? 'bg-blue-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                        >
                            {isDownloadingAll ? <FaSpinner className="animate-spin w-5 h-5" /> : <FaDownload className="w-5 h-5" />}
                            Download All PDFs
                        </button>

                        <button
                            onClick={handleDeleteAllConversations}
                            disabled={isDeleting.type === 'all' || totalItems === 0} 
                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl shadow-lg font-medium text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                                ${isDeleting.type === 'all' || totalItems === 0
                                    ? 'bg-red-300 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700'
                                }`}
                        >
                            {isDeleting.type === 'all' ? <FaSpinner className="animate-spin w-5 h-5" /> : <FaTrash className="w-5 h-5" />}
                            Delete All Chats
                        </button>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <FaExclamationTriangle className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat History Table */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                {isLoading && usersData.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
                            <FaSpinner className="animate-spin h-8 w-8 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Loading Chat History</h3>
                        <p className="text-gray-600 dark:text-gray-400">Please wait while we fetch user conversations...</p>
                    </div>
                ) : !isLoading && usersData.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                            <FaUsers className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Users Found</h3>
                        <p className="text-gray-600 dark:text-gray-400">There are no user chat records to display.</p>
                    </div>
                ) : (
                    <>
                        {/* Table Header */}
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Users ({totalItems})
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* User List */}
                        <div className="divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto max-h-96">
                            {usersData.map((user) => (
                                <div key={user._id} className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                    <div
                                        className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                                        onClick={() => toggleUserExpansion(user._id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                                <FaUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <span className="text-base font-medium text-gray-900 dark:text-white">{user.name || 'Guest'}</span>
                                                {user.guestId && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 font-mono">ID: {user.guestId}</p>
                                                )}
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {user.chats?.length || 0} chat sessions
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteUser(user._id, user.name);
                                                }}
                                                disabled={isDeleting.id === user._id}
                                                className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                                title="Delete this user"
                                            >
                                                {isDeleting.id === user._id ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaTrash className="w-4 h-4" />}
                                            </button>
                                            <span className="text-sm text-gray-500 dark:text-gray-400 w-4 text-center">
                                                {expandedUser === user._id ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                                            </span>
                                        </div>
                                    </div>

                                    {expandedUser === user._id && (
                                        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                            {user.chats && user.chats.length > 0 ? (
                                                <div className="space-y-3">
                                                    {user.chats.map((chat) => (
                                                        <div key={chat.sessionId} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden shadow-sm">
                                                            <div className="p-4 flex justify-between items-center">
                                                                <div className="flex-grow flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors" onClick={() => toggleChatExpansion(chat.sessionId)}>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                                                            <FaComments className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-sm font-medium text-gray-900 dark:text-white">Session ID:</span>
                                                                            <span className="text-sm text-gray-600 dark:text-gray-400 ml-2 font-mono">{chat.sessionId}</span>
                                                                        </div>
                                                                    </div>
                                                                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                                                                        {expandedChat === chat.sessionId ? <FaChevronUp className="w-4 h-4" /> : <FaChevronDown className="w-4 h-4" />}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center space-x-2 ml-4">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleExportChatPdf(chat);
                                                                        }}
                                                                        className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200"
                                                                        title="Open PDF in new tab"
                                                                    >
                                                                        <FaFilePdf className="w-3 h-3" />
                                                                        Download
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteSession(user._id, chat.sessionId);
                                                                        }}
                                                                        disabled={isDeleting.id === chat.sessionId}
                                                                        className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                                                        title="Delete this session"
                                                                    >
                                                                        {isDeleting.id === chat.sessionId ? <FaSpinner className="animate-spin w-4 h-4" /> : <FaTrash className="w-4 h-4" />}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            {expandedChat === chat.sessionId && (
                                                                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30">
                                                                    {chat.conversation && chat.conversation.length > 0 ? (
                                                                        <div className="space-y-3">
                                                                            {chat.conversation.map((msg, msgIndex) => (
                                                                                <div key={msg._id || msgIndex} className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                                                                    <div className="space-y-2">
                                                                                        <div className="flex items-start gap-2">
                                                                                            <div className="p-1 bg-blue-100 dark:bg-blue-900/20 rounded">
                                                                                                <FaComments className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                                                                            </div>
                                                                                            <div className="flex-1">
                                                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">System:</p>
                                                                                                {/* <p className="text-sm text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/10 p-3 rounded-lg border-l-4 border-blue-500">{msg.system}</p> */}
                                                                                                <div 
      className="ql-editor !p-0 text-sm font-medium text-gray-900 dark:text-white break-words"
      style={{ minHeight: 'unset' }}
      dangerouslySetInnerHTML={{ __html: msg.system }} 
    />
  </div>
                                                                                            {/* </div> */}
                                                                                        </div>
                                                                                        <div className="flex items-start gap-2">
                                                                                            <div className="p-1 bg-green-100 dark:bg-green-900/20 rounded">
                                                                                                <FaUsers className="w-3 h-3 text-green-600 dark:text-green-400" />
                                                                                            </div>
                                                                                            <div className="flex-1">
                                                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">User:</p>
                                                                                                <p className="text-sm text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/10 p-3 rounded-lg border-l-4 border-green-500 italic">{msg.user}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center py-6">
                                                                            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mb-3">
                                                                                <FaComments className="w-6 h-6 text-gray-400" />
                                                                            </div>
                                                                            <p className="text-gray-500 dark:text-gray-400 text-sm">No conversation in this session.</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 italic text-center text-sm py-2">No chat sessions for this user.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <PaginationControls />
                        </>
                    )}
                </div>
            </div>
    );
};

export default UserChatHistory;